import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faGamepad, faEarthAmericas, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

import { UserAuth } from '@/authentication/AuthContext';
import { PrimaryButton, SecondaryButton } from '@/components/button/Button.jsx';
import { StandardModal } from '@/components/modal/Modal.jsx';
import Alert from '@/components/alert/Alert.jsx';
import { Textarea } from '@/components/form/Input.jsx';
import { Select, Autocomplete } from '@/components/form/Select.jsx';
import { requestGameAccess, getRequestableGeographies } from '@/services/admin-settings/UserGameAccessService.jsx';
import { buildRoleOptions, REQUESTABLE_GAME_ROLES } from '@/utils/roleOptions.js';

import './RequestGameAccessModal.css';

/**
 * Lightweight "Request access to this game" modal, launched from a game's Request access button:
 * a card under "Other games in <org>" on the Games page, or the card a game's own page shows when
 * the user cannot open it (RouteGameAccessCard).
 *
 * The requester picks a role (discipline) — there is no implicit GAME_USER default any more,
 * and the approving owner can still override the role when granting. The game itself is
 * locked (the caller picked it), so role is the ONLY decision.
 *
 * There is deliberately no build / gameClientId picker: the client level was folded into the
 * game, {@code CreateUserGameAccessRequest} carries no client field, and the with-accessibility
 * payload no longer returns {@code builds[]} or {@code gameClientId}. Requiring one made Submit
 * unreachable for every game.
 *
 * On success the modal closes and invokes `onSubmitted` so the parent can
 * refresh the "my pending requests" state and flip the card to Pending.
 *
 * Props:
 *   isOpen        — bool
 *   onClose       — () => void   (cancel / X)
 *   onSubmitted   — () => void   (request created OK)
 *   game          — raw GameRegistryDTO from the with-accessibility endpoint
 *                   (id, orgId, gameName)
 */
// '' is the whole-game sentinel, matching GeographySelect and CombinedUserManagement's WHOLE_GAME.
// A geography is an ABAC condition on the grant, never a scope level, so "no geography" is the
// absence of a condition rather than a special geography — see the modal docblock.
const WHOLE_GAME = '';
const WHOLE_GAME_LABEL = 'Global (whole game)';

// Same builder every other role picker uses, so a requester reads the roles exactly as the owner
// approving them will. No placeholder option: the Autocomplete renders its own.
const REQUESTABLE_ROLES = buildRoleOptions({ roles: REQUESTABLE_GAME_ROLES, placeholder: null });

export default function RequestGameAccessModal({ isOpen, onClose, onSubmitted, game }) {
    const { user } = UserAuth();
    const [selectedRole, setSelectedRole] = useState('');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState(null);
    // '' = whole game. Geographies a requester may narrow to, loaded per game.
    const [geographySegmentId, setGeographySegmentId] = useState(WHOLE_GAME);
    const [geographies, setGeographies] = useState([]);
    const [geographiesLoading, setGeographiesLoading] = useState(false);

    // Reset the form whenever the modal opens for a fresh game.
    useEffect(() => {
        if (!isOpen) return;
        setSelectedRole('');
        setMessage('');
        setSubmitting(false);
        setGeographySegmentId(WHOLE_GAME);
    }, [isOpen, game?.id]);

    // The requester has no access to this game, so this comes from the request-flow endpoint
    // rather than the Segmentation-gated segment read. An empty list is normal, not an error.
    useEffect(() => {
        if (!isOpen || !game?.id || !user?.accessToken) { setGeographies([]); return undefined; }
        let cancelled = false;
        setGeographiesLoading(true);
        getRequestableGeographies({ accessToken: user.accessToken, gameId: game.id })
            .then((list) => { if (!cancelled) setGeographies(Array.isArray(list) ? list : []); })
            .finally(() => { if (!cancelled) setGeographiesLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, game?.id, user?.accessToken]);

    // "Whole game" leads and is preselected: it matches every other geography picker in the app,
    // and it is what an approved request has always granted, so the field is purely additive.
    const geographyOptions = useMemo(
        () => [{ value: WHOLE_GAME, label: WHOLE_GAME_LABEL },
            ...geographies.map((g) => ({ value: g.id, label: g.name }))],
        [geographies],
    );
    const selectedGeographyLabel = geographyOptions
        .find((o) => o.value === geographySegmentId)?.label || WHOLE_GAME_LABEL;

    // The role Autocomplete reports the typed text on keystroke and the chosen
    // option's value on select. Commit only a value that maps to a known role
    // (matched by value OR label) so a half-typed string never counts as a
    // valid selection — keeps the picker constrained while looking/searching
    // exactly like the onboarding "Job title" field.
    const handleRoleChange = (v) => {
        const match = REQUESTABLE_ROLES.find((r) => r.value === v || r.label === v);
        setSelectedRole(match ? match.value : '');
    };
    const selectedRoleLabel = REQUESTABLE_ROLES.find((r) => r.value === selectedRole)?.label || '';

    // Role is the only decision. It used to also require a selected build, which no game has
    // carried since the client level was folded into the game — so Submit was permanently
    // disabled for every game and the "Request access" affordance dead-ended here.
    const canSubmit = !submitting && !!selectedRole;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await requestGameAccess({
                accessToken: user?.accessToken,
                request: {
                    userId: user?.email,
                    // Firebase users without a displayName get a sensible
                    // fallback so the backend's @NotNull never trips and
                    // emails don't render as "(null) requested access".
                    userName: user?.displayName || user?.email,
                    // Triple is now sent explicitly so the persisted row is
                    // self-describing and the approval queue can match on
                    // gameId (registry _id) without join trickery.
                    orgId: game?.orgId,
                    gameId: game?.id,
                    role: selectedRole,
                    // Null, not '', for whole game — the backend treats blank as "no restriction"
                    // but null is the honest wire value for "no geography chosen".
                    geographySegmentId: geographySegmentId || null,
                    // No regionIds: the field exists on CreateUserGameAccessRequest but the approval
                    // path grants whole-game (GrantRole is called with a null geographySegmentId), so
                    // anything sent here was stored and then ignored. A geography-scoped request is
                    // the real feature and needs that grant call wired — see the modal docblock.
                    // NOTE: CreateUserGameAccessRequest carries no `message` field, so this is
                    // dropped server-side. Left in the payload (harmless) rather than removing the
                    // input, since surfacing it to the owner is a backend change, not a UI one.
                    message: message.trim() || undefined,
                },
            });
            setToast({ type: 'success', text: 'Request submitted. The game owner will review it shortly.' });
            setTimeout(() => {
                onSubmitted?.();
                onClose?.();
            }, 600);
        } catch (err) {
            setSubmitting(false);
            const detail = err?.response?.data?.message || err?.message || 'Unknown error';
            setToast({ type: 'error', text: `Failed to submit: ${detail}` });
        }
    };

    // Footer mirrors the Register-New-Organization pattern: a left-aligned hint for the one
    // blocking requirement, then Cancel + a filled primary CTA with a leading icon.
    const footer = (
        <>
            {!selectedRole && !submitting && (
                <span className="request-access-footer-hint">
                    Pick the role you need access as to continue.
                </span>
            )}
            <SecondaryButton onClick={onClose} disabled={submitting}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleSubmit} disabled={!canSubmit}>
                {submitting ? 'Submitting...' : (
                    <>
                        <FontAwesomeIcon icon={faPaperPlane} />
                        {' Submit request'}
                    </>
                )}
            </PrimaryButton>
        </>
    );

    return (
        <>
            <StandardModal
                isOpen={isOpen}
                onClose={onClose}
                size="large"
                width="min(640px, 92vw)"
                icon={<FontAwesomeIcon icon={faKey} />}
                title="Request Game Access"
                subtitle="Your request goes to the game owner. You'll get an email and an in-app notification once it's approved or declined."
                actions={footer}
            >
                <div className="request-access-section">
                    <div className="request-access-section-title-container">
                        <h3 className="request-access-section-title">
                            <FontAwesomeIcon icon={faKey} />
                            Access Request
                        </h3>
                        <p className="request-access-section-subtitle">
                            Choose the discipline you need on this game
                        </p>
                    </div>

                    {/* Locked summary — the game is pinned, since the caller picked it.
                        Role is the only decision left. */}
                    <div className="request-access-locked">
                        <div className="request-access-locked-row">
                            <FontAwesomeIcon icon={faGamepad} className="request-access-locked-icon" />
                            <div className="request-access-locked-text">
                                <div className="request-access-locked-label">Game</div>
                                <div className="request-access-locked-value">{game?.gameName || '—'}</div>
                            </div>
                        </div>
                    </div>

                    {/* Searchable Autocomplete (name + description per option), matching the
                        onboarding "Job title" field; portaled menu so the full list is never
                        clipped by the modal body. */}
                    <Autocomplete
                        label="Which role are you requesting?"
                        placeholder="Please select your role (e.g. Producer, Designer)"
                        helpText={selectedRole
                            ? `Requesting ${selectedRoleLabel} — the game owner can adjust this when approving.`
                            : 'Required — pick the discipline you need access as before submitting.'}
                        options={REQUESTABLE_ROLES}
                        value={selectedRole}
                        onChange={(e) => handleRoleChange(e.target.value)}
                        isRequired
                    />
                </div>

                <div className="request-access-section">
                    <div className="request-access-section-title-container">
                        <h3 className="request-access-section-title">
                            <FontAwesomeIcon icon={faEarthAmericas} />
                            Scope &amp; Context
                        </h3>
                        <p className="request-access-section-subtitle">
                            How much of the game you need, and anything the owner should know
                        </p>
                    </div>

                    {/* A game with no geographies offers no choice, so it reads as a locked line
                        rather than a one-option dropdown — the same reasoning as the Game row. */}
                    {geographiesLoading ? (
                        <div className="request-access-field">
                            <div className="request-access-field-label">Geography</div>
                            <div className="request-access-region-empty">Loading geographies…</div>
                        </div>
                    ) : geographies.length === 0 ? (
                        <div className="request-access-locked">
                            <div className="request-access-locked-row">
                                <FontAwesomeIcon icon={faEarthAmericas} className="request-access-locked-icon" />
                                <div className="request-access-locked-text">
                                    <div className="request-access-locked-label">Geography</div>
                                    <div className="request-access-locked-value">
                                        {WHOLE_GAME_LABEL}
                                        <span className="request-access-locked-hint">
                                            — none configured for this game
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Select
                            label="Geography"
                            name="geography"
                            value={geographySegmentId}
                            options={geographyOptions}
                            onChange={(e) => setGeographySegmentId(e.target.value)}
                            helpText={geographySegmentId
                                ? `Narrowed to ${selectedGeographyLabel} — the owner can widen or narrow this when approving.`
                                : 'Whole game by default. Narrow to one geography if that is all you need.'}
                            fullWidth
                        />
                    )}

                    <Textarea
                        label="Message to the game owner (optional)"
                        placeholder="A short note about why you need access — included in the owner's notification."
                        rows={3}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        maxLength={500}
                    />
                </div>
            </StandardModal>

            {toast && (
                <Alert
                    variant={toast.type}
                    title={toast.type === 'error' ? 'Request failed' : 'Request submitted'}
                    position="bottom-right"
                    dismissible
                    duration={toast.type === 'error' ? 5000 : 2500}
                    onDismiss={() => setToast(null)}
                >
                    {toast.text}
                </Alert>
            )}
        </>
    );
}
